import { useCallback, useMemo, useState } from "react";
import type { Site } from "@wix/ambassador-site-list-v2-site/build/cjs/types.impl";
import { formatIcuMessage, formatTimePastSince } from "../../../utils/utils";
import {
  useTranslation,
  useEssentials,
  usePanorama,
} from "@wix/fe-essentials-standalone";
import { PICASSO_URL } from "../../../consts";
import { useBiLogger } from "../../../bi/useBiLogger";
import { PAGE_AREA, PAGE_NAME } from "../../../bi/constants";

interface UseSiteThumbnailResult {
  handleClick: (siteId: string) => Promise<void>;
  timePastSince: { formattedUnit: any; value: number } | undefined;
  thumbnailUrl: string;
  error: string | null;
  setError: (error: string | null) => void;
}

export const useSiteThumbnail = (site: Site): UseSiteThumbnailResult => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const { environment } = useEssentials();
  const { errorMonitor } = usePanorama();
  const errorReporter = errorMonitor({
    data: { tags: { feature: "site-thumbnail" } },
  });
  const biLogger = useBiLogger();

  const handleClick = useCallback(async (siteId: string) => {
    try {
      biLogger.trackUserClickPicasso({
        pageName: PAGE_NAME.HOME_PAGE,
        pageArea: PAGE_AREA.SITES_LIST,
      });
      window.location.href = `${PICASSO_URL}/projects/${siteId}/v/editor`;
    } catch (error: any) {
      errorReporter.reportError(
        new Error(`Failed to redirect to site: ${error}`),
      );
    }
  }, []);

  const timePastSince = useMemo(() => {
    if (!site.updatedDate) {
      return undefined;
    }
    try {
      const time = formatTimePastSince(new Date(site.updatedDate));
      const translatedUnit = t(`landing.sites.time_unit_${time.unit}`);
      const formattedUnit = formatIcuMessage(
        translatedUnit,
        time.value,
        environment.language,
      );
      return { formattedUnit, value: time.value };
    } catch (error: any) {
      errorReporter.reportError(
        new Error(`Failed to get time past since: ${error}`),
      );
      return undefined;
    }
  }, [environment.language, site.updatedDate, t]);

  const thumbnailUrl = useMemo(() => {
    return site.thumbnail?.startsWith("http")
      ? site.thumbnail
      : `https://www.wix.com${site.thumbnail}`;
  }, [site.thumbnail]);

  return {
    handleClick,
    timePastSince,
    thumbnailUrl,
    error,
    setError,
  };
};
